/**
 * Tab-scoped persistence for the investigations drawer so that navigating to an investigation's
 * full page and pressing Back (or closing and reopening the drawer) lands the user where they
 * left off instead of back at the work queue with an empty population builder.
 *
 * sessionStorage is deliberate: it survives in-tab navigation but not new tabs or a closed tab,
 * so stale drafts never follow the user around.
 */
const PREFIX = "lusca-dlq-investigations:";

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function readSessionState<T>(key: string): T | undefined {
  const store = storage();
  if (!store) return undefined;
  try {
    const raw = store.getItem(PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
}

export function writeSessionState(key: string, value: unknown): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota / privacy mode: persistence is best-effort.
  }
}

export function clearSessionState(key: string): void {
  storage()?.removeItem(PREFIX + key);
}

/** One remembered view per host: the dashboard drawer and the full page have different homes. */
export const drawerViewKey = (
  stage: string,
  host: "page" | "dashboard" = "page"
) => `drawer-view:${host}:${stage}`;

export const builderDraftKey = (
  stage: string,
  seed: unknown,
  recurrenceOf?: string,
  splitFrom?: string
) =>
  `builder-draft:${stage}:${recurrenceOf ?? ""}:${
    splitFrom ? `split=${splitFrom}:` : ""
  }${JSON.stringify(seed)}`;
